import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { Dialog } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import Sample from '../../../../../../types/sample/Sample';
import { EnumLabelPipe } from '../../../../pipes/enum-label.pipe';
import { FormatDateTime } from '../../../../pipes/format-date-time';
import { FieldOfUseSeverityPipe } from '../../../../pipes/sample-field-of-use-severity.pipe';
import { SampleStateSeverityPipe } from '../../../../pipes/sample-state-severity.pipe';
import { SampleService } from '../../../../services/sample.service';

@Component({
    selector: 'app-add-samples-to-vehicle-model-dialog',
    imports: [
        Dialog,
        TableModule,
        FormsModule,
        InputTextModule,
        CardModule,
        IconField,
        InputIcon,
        Button,
        IftaLabelModule,
        FieldOfUseSeverityPipe,
        FormatDateTime,
        SampleStateSeverityPipe,
        EnumLabelPipe,
        Tag
    ],
    templateUrl: './add-samples-to-vehicle-model-dialog.component.html',
    styleUrls: [ './add-samples-to-vehicle-model-dialog.component.scss' ]
})
export class AddSamplesToVehicleModelDialogComponent implements OnChanges {

    @Input() public visible: boolean = false;
    @Input() public vehicleModelId: number | null = null;
    @Output() public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public samplesAdded = new EventEmitter<void>();

    public loading = false;
    public samples: Sample[] = [];
    public totalRecords: number = 0;
    public rows: number = 10;
    public search: string = '';
    public page: number = 0;
    public selectedSamples: Sample[] = [];

    constructor(private sampleService: SampleService, private messageService: MessageService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && this.visible) {
            // Load samples when the dialog is opened
            this.loadSamples({ first: 0, rows: this.rows });
            this.selectedSamples = [];
        }
    }

    public close() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
    }

    public loadSamples(event: TableLazyLoadEvent) {
        this.loading = true;

        const page = ( event.first || 0 ) / ( event.rows || this.rows );
        const size = event.rows || this.rows;

        this.sampleService
            .getSamples(this.search, page, size, this.vehicleModelId ? [ this.vehicleModelId ] : [])
            .then((data) => {
                this.samples = data.content;

                this.samples.forEach(sample => {
                    sample.detailNames = this.getDetailNames(sample);
                });

                this.totalRecords = data.page.totalElements;
                this.page = page;
                this.loading = false;
            })
            .catch((error) => {
                console.error('Failed to load samples:', error);
                this.loading = false;
            });
    }

    public onSearch(event: any) {
        this.search = event.target.value;
        this.loadSamples({ first: 0, rows: this.rows });
    }

    public onSelectionChange(event: any) {
        this.selectedSamples = event;
    }

    public addSamples() {
        if (this.vehicleModelId !== null && this.selectedSamples.length > 0) {
            const sampleIds = this.selectedSamples.map((sample) => sample.id);
            this.loading = true;
            this.sampleService
                .addSamplesToVehicleModel(this.vehicleModelId, sampleIds)
                .then(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Samples added to vehicle model',
                        life: 3000,
                    });
                    this.samples = [];
                    this.selectedSamples = [];
                    this.samplesAdded.emit();
                    this.close();
                })
                .catch((error) => {
                    console.error('Failed to add samples to vehicle model:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }

    public getDetailNames(sample: Sample): string {
        return sample.categoryDetails.map(detail => detail.name).join(', ');
    }

}
